/**
 * WHAT OMNΛS IS PERMITTED TO DO.
 *
 * The five levels, with the enforcement status the platform's own policy
 * states and source confirms. This is the boundary of machine authority,
 * written down where a person governing a decision can read it.
 *
 * The load-bearing sentence is the one at the bottom: **there is no runtime
 * autonomy mode.** There is no autonomy-level column anywhere in the schema
 * and nothing reads one, so this describes what the platform is permitted to
 * do — never what mode a project is in. A per-project mode badge would be a
 * runtime fact this platform does not have, and this panel deliberately
 * offers none.
 */
import { useState } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../components/ui/collapsible";
import {
  AUTONOMY_COPY,
  NO_RUNTIME_MODE_NOTE,
  presentAutonomyLevels,
  highestEnforcedLevel,
} from "./autonomyPresentation";
import { ShieldCheck, ChevronDown } from "lucide-react";

/**
 * Enforcement tone. `absent` is not red: a level nobody built is not a
 * fault, it is a boundary — and colouring it as a failure would suggest
 * something is missing that ought to be running.
 */
const ENFORCEMENT_TONE = {
  enforced: "border-indigo-500/40 text-indigo-300 bg-indigo-500/10",
  partial: "border-amber-500/40 text-amber-300 bg-amber-500/10",
  absent: "border-border text-muted-foreground bg-muted/40",
};

const AutonomyBoundaryPanel = () => {
  const [open, setOpen] = useState(false);
  const levels = presentAutonomyLevels();
  const highest = highestEnforcedLevel();

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card className="border-border" data-testid="autonomy-boundary">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left"
          >
            <span className="min-w-0">
              <span className="text-sm font-semibold flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-primary" />
                {AUTONOMY_COPY.levelsTitle}
              </span>
              <span className="text-[10px] text-muted-foreground" data-testid="highest-enforced">
                Fully enforced up to level {highest.level} — {highest.name}
              </span>
            </span>
            <ChevronDown
              className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="px-4 pb-4 space-y-2 border-t border-border pt-3">
            {levels.map((level) => (
              <div key={level.level} className="space-y-0.5" data-testid="autonomy-level">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <span className="text-xs font-medium">
                    Level {level.level} · {level.name}
                    <span className="text-muted-foreground font-normal"> — {level.label}</span>
                  </span>
                  <Badge
                    variant="outline"
                    className={`text-[10px] ${ENFORCEMENT_TONE[level.enforcementDisplay.tone] || ENFORCEMENT_TONE.absent}`}
                    data-testid="level-enforcement"
                  >
                    {level.enforcementDisplay.label}
                  </Badge>
                </div>
                <p className="text-[10px] text-muted-foreground leading-relaxed">{level.summary}</p>
                <p className="text-[10px] text-muted-foreground leading-relaxed">
                  {level.enforcementDetail}
                </p>
              </div>
            ))}
            <p
              className="text-[10px] text-muted-foreground leading-relaxed border-t border-border/60 pt-2"
              data-testid="no-runtime-mode"
            >
              {NO_RUNTIME_MODE_NOTE}
            </p>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export default AutonomyBoundaryPanel;
